import { TpDataContext } from '../interfaces';
import { getFilterStepColumnReferences } from '../selection/snapshot/stepSanitization';
import type { AddColumnTableJoin, LedgerAction } from './schemas';

/**
 * Perform the cheap, fail-whole checks required before constructing a replay shadow.
 * This deliberately does not inspect values or attempt async column loads.
 */
export function checkLedgerPreflight(targetDataContext: TpDataContext, actions: LedgerAction[]): { errors: string[] } {
  const errors: string[] = [];
  const tablesById = new Map(targetDataContext.dataTables.map((table) => [table.id, table]));
  const knownColumnsByTable = new Map(
    targetDataContext.dataTables.map((table) => [
      table.id,
      new Set(table.baseColumnIds ?? table.columns.map((column) => column.id)),
    ]),
  );

  const requireColumn = (actionIndex: number, actionType: string, tableId: string, columnId: string) => {
    const knownColumns = knownColumnsByTable.get(tableId);
    if (!knownColumns) {
      errors.push(`Action ${actionIndex} (${actionType}) references missing table ${tableId}`);
    } else if (!knownColumns.has(columnId)) {
      errors.push(`Action ${actionIndex} (${actionType}) references missing column ${columnId} in table ${tableId}`);
    }
  };

  const checkFilter = (actionIndex: number, filter: AddColumnTableJoin['sourceFilter']) => {
    for (const step of filter?.steps ?? []) {
      for (const reference of getFilterStepColumnReferences(step)) {
        requireColumn(actionIndex, 'TABLE_JOIN', reference.dataTableId, reference.columnId);
      }
    }
  };

  actions.forEach((action, actionIndex) => {
    const actionType = action.type === 'DELETE_COLUMN' ? action.type : action.payload.type;
    if (!tablesById.has(action.targetTableId)) {
      errors.push(`Action ${actionIndex} (${actionType}) references missing target table ${action.targetTableId}`);
      return;
    }

    if (action.type !== 'DELETE_COLUMN') {
      const payload = action.payload;
      switch (payload.type) {
        case 'TABLE_JOIN':
          requireColumn(actionIndex, payload.type, payload.sourceTableId, payload.sourceMatchingColId);
          requireColumn(actionIndex, payload.type, payload.destTableId, payload.destMatchingColId);
          for (const column of payload.columns) {
            requireColumn(actionIndex, payload.type, payload.sourceTableId, column.sourceColId);
          }
          checkFilter(actionIndex, payload.sourceFilter);
          break;
        case 'PROPERTY_GROUP_COLUMN': {
          const sourceTable = tablesById.get(payload.sourceTableId);
          if (!sourceTable) {
            errors.push(`Action ${actionIndex} (${payload.type}) references missing table ${payload.sourceTableId}`);
          } else if (!sourceTable.columnGroups.some((group) => group.id === payload.propertyGroup)) {
            errors.push(
              `Action ${actionIndex} (${payload.type}) references missing column group ${payload.propertyGroup} in table ${payload.sourceTableId}`,
            );
          }
          break;
        }
        case 'EXPRESSION':
          for (const columnId of payload.usedColIds) {
            requireColumn(actionIndex, payload.type, action.targetTableId, columnId);
          }
          break;
        case 'ASYNC_COLUMN':
          break;
      }
    }

    const knownColumns = knownColumnsByTable.get(action.targetTableId);
    action.targetColumnIds.forEach((columnId) => knownColumns?.add(columnId));
  });

  return { errors };
}
